import { Link } from 'react-router-dom'
import type { PatientLabOrder } from '../../store/patientDetailSlice'

interface LabResultsTabProps {
  labOrders: PatientLabOrder[]
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const datePart = dateStr.split('T')[0]
  const d = new Date(datePart + 'T00:00:00')
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function getStatusStyle(status: string): string {
  switch (status) {
    case 'Reviewed':
      return 'bg-accent-subtle-bg text-status-success'
    case 'Results Received':
      return 'bg-[#f0e8d0] text-status-warning'
    case 'Sent to Lab':
      return 'bg-[#dae8f0] text-status-info'
    case 'Sample Collected':
      return 'bg-[#e8e6e2] text-text-secondary'
    case 'Ordered':
      return 'bg-border text-text-muted'
    default:
      return 'bg-border text-text-muted'
  }
}

export function LabResultsTab({ labOrders }: LabResultsTabProps) {
  return (
    <div data-testid="lab-results-tab">
      {labOrders.length === 0 ? (
        <p className="text-[13px] text-text-muted py-4" data-testid="no-lab-results">
          No lab results
        </p>
      ) : (
        <div className="grid grid-cols-1" style={{ gridTemplateRows: '36px repeat(auto-fill, 44px)' }}>
          <div className="lab-table-header grid items-center h-9 text-[12px] font-medium text-text-muted" style={{ gridTemplateColumns: '1fr 100px 100px 130px 1fr' }}>
            <span className="px-2">Test Name</span>
            <span className="px-2">Date Ordered</span>
            <span className="px-2">Date Completed</span>
            <span className="px-2">Status</span>
            <span className="px-2">Result Summary</span>
          </div>
          {labOrders.map((lo) => {
            const labId = `LAB-${String(lo.lab_order_number).padStart(4, '0')}`
            return (
              <Link
                key={lo.id}
                to={`/lab-work/${lo.id}`}
                data-testid="lab-result-item"
                className="lab-table-row grid items-center h-11 text-[13px] text-text-primary hover:bg-hover rounded-[4px] no-underline cursor-pointer"
                style={{ gridTemplateColumns: '1fr 100px 100px 130px 1fr' }}
              >
                <span className="px-2" data-testid="lab-test-name">
                  <span className="text-text-muted font-[450] mr-2">{labId}</span>
                  {lo.test_name}
                </span>
                <span className="px-2 text-text-muted" data-testid="lab-date-ordered">{formatDate(lo.date_ordered)}</span>
                <span className="px-2 text-text-muted" data-testid="lab-date-completed">{lo.date_completed ? formatDate(lo.date_completed) : '—'}</span>
                <span className="px-2">
                  <span
                    data-testid="lab-status-badge"
                    className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getStatusStyle(lo.status)}`}
                  >
                    {lo.status}
                  </span>
                </span>
                <span className="px-2 text-text-muted truncate" data-testid="lab-result-summary">{lo.results || '—'}</span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
